import React, { useState, useEffect, useRef } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyProjects } from '../../services/projectService';
import { getBOMByProject, createBOMItem, getProjectBudget, updateBOMItem, deleteBOMItem } from '../../services/bomService';
import { useToast } from '../../context/ToastContext';
import styles from './MentorBOM.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const EMPTY_ROW = () => ({
  _tempId: Math.random().toString(36).slice(2),
  item_name: '',
  quantity: 1,
  cost: 0,
  vendor: '',
  link: '',
  notes: '',
  status: 'pending',
  _isNew: true,
  _saving: false,
  _dirty: false,
});

const STATUS_OPTIONS = ['pending', 'received'];

const MentorBOM = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [currentProject, setCurrentProject] = useState(null);
  const [rows, setRows] = useState([]);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRowId, setSavingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const toast = useToast();
  const tableRef = useRef(null);

  const isProjectFrozen = () =>
    currentProject && (currentProject.status === 'on_hold' || currentProject.status === 'cancelled');

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchBOM(selectedProject);
      fetchBudget(selectedProject);
      const project = projects.find(p => p.id === selectedProject);
      setCurrentProject(project);
    } else {
      setCurrentProject(null);
      setRows([]);
      setBudget(null);
    }
  }, [selectedProject, projects]);

  const fetchProjects = async () => {
    try {
      const data = await getMyProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBOM = async (projectId) => {
    try {
      const data = await getBOMByProject(projectId);
      setRows(data.map(item => ({ ...item, _isNew: false, _saving: false, _dirty: false })));
    } catch (error) {
      console.error('Error fetching BOM:', error);
    }
  };

  const fetchBudget = async (projectId) => {
    try {
      const data = await getProjectBudget(projectId);
      setBudget(data);
    } catch (error) {
      console.error('Error fetching budget:', error);
    }
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, EMPTY_ROW()]);
    setTimeout(() => {
      if (tableRef.current) {
        tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 50);
  };

  const handleCellChange = (rowKey, field, value) => {
    setRows(prev =>
      prev.map(r => {
        const key = r._isNew ? r._tempId : r.id;
        if (key === rowKey) return { ...r, [field]: value, _dirty: true };
        return r;
      })
    );
  };

  const handleSaveRow = async (row) => {
    const rowKey = row._isNew ? row._tempId : row.id;
    if (!row.item_name.trim()) {
      toast.warning('Item name is required');
      return;
    }
    setSavingRowId(rowKey);
    const payload = {
      item_name: row.item_name,
      quantity: Number(row.quantity),
      cost: Number(row.cost),
      vendor: row.vendor || '',
      link: row.link || '',
      notes: row.notes || '',
      status: row.status,
    };
    try {
      if (row._isNew) {
        const created = await createBOMItem({ ...payload, project_id: selectedProject });
        setRows(prev =>
          prev.map(r => (r._tempId === row._tempId ? { ...created, _isNew: false, _saving: false, _dirty: false } : r))
        );
        toast.success('Row added!');
      } else {
        const updated = await updateBOMItem(row.id, payload);
        setRows(prev =>
          prev.map(r => (r.id === row.id ? { ...updated, _isNew: false, _saving: false, _dirty: false } : r))
        );
        toast.success('Row updated!');
      }
      fetchBudget(selectedProject);
    } catch (error) {
      toast.error('Error saving row: ' + error.message);
    } finally {
      setSavingRowId(null);
    }
  };

  const handleDeleteRow = async (row) => {
    const rowKey = row._isNew ? row._tempId : row.id;
    if (row._isNew) {
      setRows(prev => prev.filter(r => r._tempId !== row._tempId));
      return;
    }
    setDeletingRowId(rowKey);
    try {
      await deleteBOMItem(row.id);
      setRows(prev => prev.filter(r => r.id !== row.id));
      fetchBudget(selectedProject);
      toast.success('Row deleted');
    } catch (error) {
      toast.error('Error deleting row: ' + error.message);
    } finally {
      setDeletingRowId(null);
    }
  };

  const totalCost = rows.reduce((sum, r) => sum + (Number(r.cost) * Number(r.quantity)), 0);

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.bomPage}>
      <div className={styles.header}>
        <h1>Bill of Materials</h1>
        <Button
          icon="+"
          onClick={handleAddRow}
          disabled={!selectedProject || isProjectFrozen()}
        >
          Add Row
        </Button>
      </div>

      {isProjectFrozen() && (
        <Card>
          <p style={{ color: '#F44336', fontWeight: 'bold', textAlign: 'center' }}>
            Alert: This project is {currentProject.status === 'on_hold' ? 'ON HOLD' : 'CANCELLED'}.
            You cannot create or modify BOM items.
          </p>
        </Card>
      )}

      <div className={styles.filterSection}>
        <div className={styles.formGroup}>
          <label>Select Project</label>
          <select
            value={selectedProject}
            onChange={(e) => setSelectedProject(e.target.value)}
            className={styles.select}
          >
            <option value="">-- Select a Project --</option>
            {sortProjectsByName(projects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedProject && (
        <Card>
          <p className={styles.empty}>Please select a project to view Bill of Materials</p>
        </Card>
      )}

      {selectedProject && (
        <>
          {/* Budget summary bar */}
          {budget && (
            <div className={styles.budgetBar}>
              <div className={styles.budgetStat}>
                <span className={styles.budgetLabel}>Total Items</span>
                <span className={styles.budgetValue}>{rows.length}</span>
              </div>
              <div className={styles.budgetDivider} />
              <div className={styles.budgetStat}>
                <span className={styles.budgetLabel}>Total Budget</span>
                <span className={styles.budgetValue}>Rs. {totalCost.toFixed(2)}</span>
              </div>
              <div className={styles.budgetDivider} />
              <div className={styles.budgetStat}>
                <span className={styles.budgetLabel}>Approved Budget</span>
                <span className={styles.budgetValue}>Rs. {budget.approved_budget ?? '—'}</span>
              </div>
            </div>
          )}

          {/* Spreadsheet table */}
          <div className={styles.sheetWrapper}>
            <table className={styles.sheet} ref={tableRef}>
              <thead>
                <tr>
                  <th className={styles.thIdx}>#</th>
                  <th className={styles.thName}>Item Name *</th>
                  <th className={styles.thNum}>Qty *</th>
                  <th className={styles.thNum}>Unit Cost (Rs.) *</th>
                  <th className={styles.thNum}>Total (Rs.)</th>
                  <th className={styles.thMed}>Vendor</th>
                  <th className={styles.thMed}>Link</th>
                  <th className={styles.thStatus}>Status</th>
                  <th className={styles.thNotes}>Notes</th>
                  <th className={styles.thActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={styles.emptyRow}>
                      No items yet. Click <strong>+ Add Row</strong> to get started.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => {
                    const rowKey = row._isNew ? row._tempId : row.id;
                    const isSaving = savingRowId === rowKey;
                    const isDeleting = deletingRowId === rowKey;
                    const rowTotal = (Number(row.cost) * Number(row.quantity)).toFixed(2);

                    return (
                      <tr
                        key={rowKey}
                        className={`${styles.dataRow} ${row._isNew ? styles.newRow : ''} ${row._dirty ? styles.dirtyRow : ''}`}
                      >
                        <td className={styles.tdIdx}>{idx + 1}</td>

                        <td className={styles.tdName}>
                          <input
                            className={styles.cellInput}
                            value={row.item_name}
                            onChange={e => handleCellChange(rowKey, 'item_name', e.target.value)}
                            placeholder="Item name"
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdNum}>
                          <input
                            className={`${styles.cellInput} ${styles.numInput}`}
                            type="number"
                            min="1"
                            value={row.quantity}
                            onChange={e => handleCellChange(rowKey, 'quantity', e.target.value)}
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdNum}>
                          <input
                            className={`${styles.cellInput} ${styles.numInput}`}
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.cost}
                            onChange={e => handleCellChange(rowKey, 'cost', e.target.value)}
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdTotal}>Rs. {rowTotal}</td>

                        <td className={styles.tdMed}>
                          <input
                            className={styles.cellInput}
                            value={row.vendor}
                            onChange={e => handleCellChange(rowKey, 'vendor', e.target.value)}
                            placeholder="Vendor"
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdMed}>
                          <input
                            className={styles.cellInput}
                            type="url"
                            value={row.link}
                            onChange={e => handleCellChange(rowKey, 'link', e.target.value)}
                            placeholder="https://..."
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdStatus}>
                          <select
                            className={`${styles.cellInput} ${styles.statusSelect} ${styles[`status_${row.status}`]}`}
                            value={row.status}
                            onChange={e => handleCellChange(rowKey, 'status', e.target.value)}
                            disabled={isProjectFrozen() || isSaving}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td className={styles.tdNotes}>
                          <input
                            className={styles.cellInput}
                            value={row.notes}
                            onChange={e => handleCellChange(rowKey, 'notes', e.target.value)}
                            placeholder="Notes..."
                            disabled={isProjectFrozen() || isSaving}
                          />
                        </td>

                        <td className={styles.tdActions}>
                          <button
                            className={`${styles.actionBtn} ${styles.saveBtn}`}
                            onClick={() => handleSaveRow(row)}
                            disabled={isProjectFrozen() || isSaving || isDeleting || (!row._dirty && !row._isNew)}
                            title="Save row"
                          >
                            {isSaving ? 'Pending' : 'Save'}
                          </button>
                          <button
                            className={`${styles.actionBtn} ${styles.deleteBtn}`}
                            onClick={() => handleDeleteRow(row)}
                            disabled={isProjectFrozen() || isSaving || isDeleting}
                            title="Delete row"
                          >
                            {isDeleting ? 'Pending' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {/* Footer totals row */}
              {rows.length > 0 && (
                <tfoot>
                  <tr className={styles.totalsRow}>
                    <td colSpan={4} className={styles.totalsLabel}>Grand Total</td>
                    <td className={styles.totalsValue}>Rs. {totalCost.toFixed(2)}</td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              )}
            </table>

            {/* Add row button inside sheet */}
            {!isProjectFrozen() && (
              <button className={styles.addRowBtn} onClick={handleAddRow}>
                <span>+</span> Add Row
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MentorBOM;